/**
 * Avisos y preferencias: el cliente de `/api/mis/notificaciones` y
 * `/api/mis/preferencias`.
 *
 * LO QUE HAY QUE SABER ANTES DE TOCAR ESTO: hoy el ÚNICO canal que entrega algo
 * es `en_app`. Correo y WhatsApp se encolan en el servidor y ahí se quedan
 * —no hay despachador y no lo habrá mientras no existan credenciales con que
 * probarlo—, así que encender uno de esos dos no produce ningún envío. La
 * pantalla lo dice; callarlo haría creer que un aviso salió cuando no salió.
 */

import {
  ErrorApi,
  ErrorDeFormato,
  describirFalloDeRed,
  esRegistro,
  leerJson,
  mensajeDelCuerpo,
} from './errores'

const TIEMPO_LIMITE_MS = 5000

export const CLAVE_NOTIFICACIONES = ['mis', 'notificaciones'] as const
export const CLAVE_PREFERENCIAS = ['mis', 'preferencias'] as const

export type Canal = 'en_app' | 'correo' | 'whatsapp' | 'sms'

/** Los canales que de verdad entregan algo hoy. */
export const CANALES_QUE_ENVIAN: readonly Canal[] = ['en_app']

export const NOMBRE_CANAL: Record<Canal, string> = {
  en_app: 'Dentro de la aplicación',
  correo: 'Correo',
  whatsapp: 'WhatsApp',
  sms: 'Mensaje de texto',
}

export interface Aviso {
  id: number
  tipo: string
  descripcion: string
  payload: Record<string, unknown>
  programada_para: string
  leida_en: string | null
}

export interface Avisos {
  notificaciones: Aviso[]
  sin_leer: number
}

export interface Preferencia {
  tipo: string
  descripcion: string
  dispara: string
  canal: Canal
  activo: boolean
  /** `false` = lo hereda de su rol y no lo ha elegido esta persona. */
  es_propia: boolean
}

export async function consultarAvisos(soloSinLeer = false): Promise<Avisos> {
  const cuerpo = await pedir(
    `/api/mis/notificaciones${soloSinLeer ? '?sin_leer=1' : ''}`,
  )
  if (!esRegistro(cuerpo)) {
    throw new ErrorDeFormato('La respuesta de notificaciones no es un objeto.')
  }
  return {
    notificaciones: exigirLista(cuerpo['notificaciones']).map(interpretarAviso),
    sin_leer: typeof cuerpo['sin_leer'] === 'number' ? cuerpo['sin_leer'] : 0,
  }
}

/** Sin `ids` marca todas: es el botón «marcar todo como leído». */
export async function marcarLeidas(ids?: number[]): Promise<void> {
  await pedir('/api/mis/notificaciones/leidas', {
    method: 'POST',
    body: JSON.stringify(ids === undefined ? {} : { ids }),
  })
}

export async function consultarPreferencias(): Promise<Preferencia[]> {
  const cuerpo = await pedir('/api/mis/preferencias')
  if (!esRegistro(cuerpo)) {
    throw new ErrorDeFormato('La respuesta de preferencias no es un objeto.')
  }
  return exigirLista(cuerpo['preferencias']).map(interpretarPreferencia)
}

export async function fijarPreferencia(
  tipo: string,
  canal: Canal,
  activo: boolean,
): Promise<void> {
  await pedir('/api/mis/preferencias', {
    method: 'PUT',
    body: JSON.stringify({ tipo, canal, activo }),
  })
}

function interpretarAviso(fila: unknown): Aviso {
  if (!esRegistro(fila)) throw new ErrorDeFormato('Un aviso no es un objeto.')
  return {
    id: Number(fila['id']),
    tipo: exigirTexto(fila, 'tipo'),
    descripcion: exigirTexto(fila, 'descripcion'),
    payload: esRegistro(fila['payload']) ? fila['payload'] : {},
    programada_para: exigirTexto(fila, 'programada_para'),
    leida_en: typeof fila['leida_en'] === 'string' ? fila['leida_en'] : null,
  }
}

function interpretarPreferencia(fila: unknown): Preferencia {
  if (!esRegistro(fila)) throw new ErrorDeFormato('Una preferencia no es un objeto.')
  const canal = fila['canal']
  // AÑADIR UN CANAL EN LA BASE OBLIGA A AÑADIRLO AQUÍ, y olvidarlo no degrada:
  // ROMPE. Esta lectura es estricta a propósito, así que un canal que el
  // servidor devuelve y esta lista no conoce hace fallar la sección ENTERA de
  // preferencias, no solo esa casilla. Pasó de verdad al añadir `sms` en la
  // migración 027: la pantalla se quedó sin una sola casilla.
  //
  // La lista se queda estricta —una divergencia silenciosa sería peor— pero
  // queda dicho aquí para que el siguiente canal no cueste lo mismo.
  if (
    canal !== 'en_app' &&
    canal !== 'correo' &&
    canal !== 'whatsapp' &&
    canal !== 'sms'
  ) {
    throw new ErrorDeFormato(`Canal desconocido: ${String(canal)}.`)
  }
  return {
    tipo: exigirTexto(fila, 'tipo'),
    descripcion: exigirTexto(fila, 'descripcion'),
    dispara: exigirTexto(fila, 'dispara'),
    canal,
    activo: fila['activo'] === true,
    es_propia: fila['es_propia'] === true,
  }
}

function exigirLista(valor: unknown): unknown[] {
  if (!Array.isArray(valor)) {
    throw new ErrorDeFormato('El servidor no devolvió una lista.')
  }
  return valor
}

function exigirTexto(registro: Record<string, unknown>, clave: string): string {
  const valor = registro[clave]
  if (typeof valor !== 'string' || valor.trim() === '') {
    throw new ErrorDeFormato(`Llegó un registro sin el campo \`${clave}\`.`)
  }
  return valor
}

async function pedir(ruta: string, opciones?: RequestInit): Promise<unknown> {
  let respuesta: Response
  try {
    respuesta = await fetch(ruta, {
      headers: {
        accept: 'application/json',
        ...(opciones?.body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      credentials: 'same-origin',
      signal: AbortSignal.timeout(TIEMPO_LIMITE_MS),
      ...opciones,
    })
  } catch (error) {
    throw new Error(`No se pudo contactar al servidor (${describirFalloDeRed(error)}).`)
  }

  const cuerpo = await leerJson(respuesta)
  if (!respuesta.ok) {
    throw new ErrorApi(
      respuesta.status,
      mensajeDelCuerpo(cuerpo) ?? `El servidor respondió ${respuesta.status} en ${ruta}.`,
    )
  }
  return cuerpo
}
